import React, { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { Icon } from "../../ds2/components";
import {
  selectEmployeeProfileSidebarIsOpen,
  selectEmployeeProfileSidebarEmployeeId,
  selectEmployeeProfileSidebarData,
  selectEmployeeProfileSidebarIsLoading,
  selectEmployeeProfileSidebarType,
  closeEmployeeProfileSidebar,
  setEmployeeProfileSidebarData,
  selectCanvasModeIsOpen,
  closeCanvasMode,
  selectEmployees,
  selectEmployeeRecords,
} from "../../redux/features/agent/agentSlice";
import {
  EmployeeProfile,
  EmployeeRecord,
  EmployeeReferenceType,
} from "winslow-core";
import { EmployeeProfileContent } from "./EmployeeProfileContent";
import "./EmployeeProfileSidebar.css";

export interface EmployeeProfileSidebarProps {
  className?: string;
}

export const EmployeeProfileSidebar: React.FC<EmployeeProfileSidebarProps> = ({
  className = "",
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isOpen = useSelector(selectEmployeeProfileSidebarIsOpen);
  const employeeId = useSelector(selectEmployeeProfileSidebarEmployeeId);
  const employeeData = useSelector(selectEmployeeProfileSidebarData);
  const isLoading = useSelector(selectEmployeeProfileSidebarIsLoading);
  const profileType = useSelector(selectEmployeeProfileSidebarType);
  const employees = useSelector(selectEmployees);
  const employeeRecords = useSelector(selectEmployeeRecords);
  const isCanvasModeOpen = useSelector(selectCanvasModeIsOpen);

  // Find the current employee record if type is employee-record
  const currentEmployeeRecord = employeeRecords?.find(
    (er) => er.id === employeeId
  );

  // Find the employee data from the existing employees array
  const employee = employees.find((e) => e.id === employeeId);

  // Convert Employee to EmployeeProfile format
  const convertEmployeeToProfile = useCallback(
    (emp: typeof employee): EmployeeProfile => {
      if (!emp) {
        return {
          id: employeeId || "",
          email: "",
          displayName: "Unknown Employee",
          avatar: "",
        } as EmployeeProfile;
      }

      return {
        id: emp.id,
        email: emp.email,
        displayName: emp.displayName || emp.email,
        title: emp.profile?.title || undefined,
        department: emp.profile?.department || undefined,
        location: emp.profile?.location || undefined,
        country: emp.profile?.country || undefined,
        office: emp.profile?.office || undefined,
        startDate: emp.profile?.startDate || undefined,
        managerName: emp.profile?.managerName || undefined,
        avatar: emp.profile?.avatar || "",
      } as EmployeeProfile;
    },
    [employeeId]
  );

  // Create unknown profile for when employee is not found
  const createUnknownProfile = useCallback((): EmployeeProfile => {
    return {
      id: employeeId || "",
      email: "",
      displayName: "Unknown Employee",
      avatar: "",
    } as EmployeeProfile;
  }, [employeeId]);

  // Close sidebar on navigation
  useEffect(() => {
    if (isOpen) {
      dispatch(closeEmployeeProfileSidebar());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, dispatch]);

  // Close Canvas Mode when Employee Profile opens (mutual exclusivity)
  useEffect(() => {
    if (isOpen && isCanvasModeOpen) {
      dispatch(closeCanvasMode());
    }
  }, [isOpen, isCanvasModeOpen, dispatch]);

  // Load employee profile data from existing Redux state
  useEffect(() => {
    if (isOpen && employeeId) {
      // Check if we need to load new data (no data or data is for different employee)
      const needsNewData = !employeeData || employeeData.id !== employeeId;

      if (needsNewData) {
        if (profileType === EmployeeReferenceType.EMPLOYEE_RECORD) {
          // For EmployeeRecord, we'll pass it directly to the content component
          // No need to convert to EmployeeProfile - just set a minimal profile for ID tracking
          const employeeRecord = employeeRecords?.find(
            (er) => er.id === employeeId
          );
          if (employeeRecord) {
            // Set minimal profile data just for tracking the ID
            const minimalProfile = {
              id: employeeRecord.id,
              email: employeeRecord.workEmail,
              displayName: employeeRecord.workEmail,
              avatar: employeeRecord.avatarURL || "",
            } as EmployeeProfile;
            dispatch(setEmployeeProfileSidebarData(minimalProfile));
          } else {
            console.warn("Employee record not found:", employeeId);
            const unknownProfile = createUnknownProfile();
            dispatch(setEmployeeProfileSidebarData(unknownProfile));
          }
        } else {
          // Find employee (existing logic)
          if (employee) {
            const profileData = convertEmployeeToProfile(employee);
            dispatch(setEmployeeProfileSidebarData(profileData));
          } else {
            console.warn("Employee not found:", employeeId);
            const unknownProfile = createUnknownProfile();
            dispatch(setEmployeeProfileSidebarData(unknownProfile));
          }
        }
      }
    }
  }, [
    isOpen,
    employeeId,
    employeeData,
    profileType,
    employee,
    employeeRecords,
    convertEmployeeToProfile,
    createUnknownProfile,
    dispatch,
  ]);

  // Handle close
  const handleClose = useCallback(() => {
    dispatch(closeEmployeeProfileSidebar());
  }, [dispatch]);

  const handleCloseKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClose();
      }
    },
    [handleClose]
  );

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      // Add body class for layout adjustments
      document.body.classList.add("employee-profile-sidebar-open");
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("employee-profile-sidebar-open");
    };
  }, [isOpen, handleClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`employee-profile-sidebar ${isOpen ? "employee-profile-sidebar-open" : ""} ${className}`}>
      {/* Header */}
      <div className="employee-profile-sidebar-header">
        <Icon
          icon={<X size={20} />}
          variant="secondary"
          onClick={handleClose}
          onKeyDown={handleCloseKeyDown}
          aria-label="Close employee profile"
          role="button"
          tabIndex={0}
          title="Close employee profile"
          className="employee-profile-sidebar-close-icon"
          style={{
            width: "36px",
            height: "36px",
            padding: 0,
            backgroundColor: "#ffffff",
            borderRadius: "9999px",
            border: "1px solid var(--secondary-button-color)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      </div>

      {/* Content */}
      <div className="employee-profile-sidebar-content">
        {isLoading ? (
          <div className="employee-profile-sidebar-loading">
            <div className="loading-spinner" />
            <span>Loading employee profile...</span>
          </div>
        ) : employeeData || currentEmployeeRecord ? (
          <EmployeeProfileContent
            employee={
              profileType === EmployeeReferenceType.EMPLOYEE_RECORD
                ? null
                : employeeData
            }
            employeeRecord={
              profileType === EmployeeReferenceType.EMPLOYEE_RECORD
                ? currentEmployeeRecord
                : null
            }
          />
        ) : (
          <div className="employee-profile-sidebar-error">
            <p>Unable to load employee profile.</p>
          </div>
        )}
      </div>
    </div>
  );
};
